import React from "react";
import { MapActions } from "@/features/map/components/MapView/MapActions";
import { MapPinRequestToggle } from "@/features/map/components/MapView/MapPinRequestToggle";
import { MapRoomPickingHud } from "@/features/map/components/MapView/MapRoomPickingHud";
import { MapViewEditorPanel } from "@/features/map/components/MapView/MapViewEditorPanel";
import { MapViewSvg } from "@/features/map/components/MapView/MapViewSvg/MapViewSvg";
import { NewMapEditor } from "@/features/map/components/NewMapEditor";
import { useMap } from "@/features/map/MapProvider";
import { ResourceSettingsProvider } from "@/features/reserve/hooks/useResourceSettings";
import { UseMapViewSvgEditReturn } from "./MapViewSvg/useMapViewSvgEdit";

export interface MapViewProps {
  embedded?: boolean;
  editor?: UseMapViewSvgEditReturn;
  isModal?: boolean;
  isOnReservationModal?: boolean;
}

export const MapView: React.FC<MapViewProps> = ({
  isOnReservationModal,
  embedded,
  editor,
  isModal
}) => {
  const {
    floor,
    isMapBuilderMap,
    forceMapBuilder,
    updateFloorMapData,
    editingMapDisclosure,
    fullscreenMapDisclosure
  } = useMap();

  return (
    <React.Fragment key={forceMapBuilder ? "forceMapBuilder" : "default"}>
      <div className="full-map-container relative flex h-full flex-row justify-between">
        <div className="isolate w-full">
          {/* The Map */}
          <MapViewSvg
            editor={editor}
            embedded={embedded}
            isOnReservationModal={isOnReservationModal}
          />
          {/* Legacy-map resource editing only. When the map builder is
              open it renders its own integrated resources panel inside
              its full-screen portal — this slide-over would sit invisibly
              underneath it (page stacking context vs body portal). */}
          {editor?.isResourcePanelEditorOpen &&
            !editor?.isMapBuilderEditorOpen && (
              <ResourceSettingsProvider>
                <MapViewEditorPanel
                  editor={editor}
                  onMapEditorSaved={() => null}
                />
              </ResourceSettingsProvider>
            )}
          {editor?.isMapBuilderEditorOpen && floor && isMapBuilderMap && (
            <NewMapEditor
              editorRef={editor?.setMMapBuilderRef as never}
              floor={floor}
              key={floor.id}
              setEditingResources={(value) => {
                // Store in pick order, verbatim. Reversing here scrambled
                // the list whenever the resources panel appended a search
                // pick to the already-stored ids (each add re-reversed the
                // whole selection); bulk numbering handles ordering itself.
                editor.setMapResourcesEditOptions({
                  isOpen: value.isOpen,
                  resourceIds: [...(value.resourceIds || [])]
                });
              }}
              editingResources={editor.mapResourcesEditOptions}
              onSaved={updateFloorMapData}
              onExit={() => {
                editingMapDisclosure.close();
                editor?.closeMapBuilderEditor();
              }}
            />
          )}
          {!!floor && !isModal && !editor?.isMapBuilderEditorOpen && (
            <>
              {/* Anchored to the map area; offset past the 440px filters sidebar,
                  but slides fully left once the sidebar is hidden (fullscreen). */}
              <div
                className={`absolute top-4 left-4 z-10 hidden transition-[left] duration-200 md:block ${
                  fullscreenMapDisclosure.isOpen
                    ? "md:left-4"
                    : "md:left-[456px]"
                }`}
              >
                <MapPinRequestToggle />
              </div>
              <div className={"fixed top-4 right-1"}>
                <MapActions editor={editor} />
              </div>
            </>
          )}
          {/* Room picking is available in the reservation modal too, so this one is
              not tied to the `!isModal` chrome above. */}
          {!!floor && !editor?.isMapBuilderEditorOpen && (
            <div className="-translate-x-1/2 absolute bottom-6 left-1/2 z-10">
              <MapRoomPickingHud />
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};
